import { useEffect, useState, useCallback } from 'react';

/**
 * Lógica do convite "instalar o app" (PWA).
 *
 * - Android/Chrome: captura o evento `beforeinstallprompt` e expõe `promptInstall()`,
 *   que dispara o instalador nativo.
 * - iOS/Safari: não existe API de instalação — só dá pra mostrar instrução manual
 *   (Compartilhar -> "Adicionar à Tela de Início"). `platform` vira `'ios'`.
 * - Já instalado (rodando em standalone) ou dispensado recentemente: `canShow` = false.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'astralyz_install_dismissed_at';
const INSTALLED_KEY = 'astralyz_install_done';
const SNOOZE_MS = 30 * 24 * 60 * 60 * 1000; // 30 dias
const SHOW_DELAY_MS = 4000;

type Platform = 'android' | 'ios' | null;

function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // iOS Safari
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function detectIOSSafari(): boolean {
  const ua = window.navigator.userAgent;
  const iOS = /iPhone|iPad|iPod/.test(ua) ||
    // iPadOS 13+ se apresenta como Mac com toque
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  // Chrome/Firefox/Edge no iOS não conseguem instalar
  const realSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return iOS && realSafari;
}

function wasDismissedRecently(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    return Date.now() - Number(raw) < SNOOZE_MS;
  } catch {
    return false;
  }
}

function wasInstalled(): boolean {
  try {
    return localStorage.getItem(INSTALLED_KEY) === '1';
  } catch {
    return false;
  }
}

export function useInstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [platform, setPlatform] = useState<Platform>(null);
  const [ready, setReady] = useState(false); // passou o delay inicial
  const [gone, setGone] = useState(false); // instalou ou dispensou nesta sessão

  useEffect(() => {
    if (isStandalone() || wasInstalled() || wasDismissedRecently()) return;

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setPlatform('android');
    };

    const onInstalled = () => {
      try {
        localStorage.setItem(INSTALLED_KEY, '1');
      } catch {
        /* storage indisponível — ok */
      }
      setGone(true);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    // iOS não dispara beforeinstallprompt — decide na hora
    if (detectIOSSafari()) setPlatform('ios');

    const t = window.setTimeout(() => setReady(true), SHOW_DELAY_MS);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      window.clearTimeout(t);
    };
  }, []);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* storage indisponível — ok */
    }
    setGone(true);
  }, []);

  const promptInstall = useCallback(async (): Promise<'accepted' | 'dismissed' | 'unavailable'> => {
    if (!deferred) return 'unavailable';
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    setDeferred(null);
    if (outcome === 'dismissed') dismiss();
    else setGone(true);
    return outcome;
  }, [deferred, dismiss]);

  const canShow =
    ready &&
    !gone &&
    ((platform === 'android' && !!deferred) || platform === 'ios');

  return { canShow, platform, promptInstall, dismiss };
}
